import React, { useCallback, useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Button } from './ui';

/**
 * Detects when a newer version is available and prompts the user to update.
 *
 * Two distinct checks, branched by platform:
 * - Web/PWA: each production build stamps a unique `buildId` into the bundle
 *   (__BUILD_ID__) and writes the same id to /version.json. We poll that file;
 *   if it differs from the running build, a new version is live. A reload
 *   pulls it in (index.html is served no-cache and JS bundles are content-hashed).
 * - Native iOS: the app is bundled at build time (no live server URL), so a
 *   reload can't pull in new code. Instead we compare the installed build's
 *   version (__APP_VERSION__, synced from package.json into Info.plist by
 *   scripts/set-ios-version.js) against the version currently live on the App
 *   Store, via Apple's public iTunes lookup API. If Apple's published version
 *   is newer, we prompt the user to update from the App Store.
 */
const VERSION_URL = '/version.json';
const DISMISS_KEY = 'update-prompt-dismissed';
const NATIVE_DISMISS_KEY = 'native-update-prompt-dismissed';

const APP_STORE_ID = '6753561309';
const APP_STORE_URL = `https://apps.apple.com/app/id${APP_STORE_ID}`;
const ITUNES_LOOKUP_URL = `https://itunes.apple.com/lookup?id=${APP_STORE_ID}`;

const isNativeIOS = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';

// Compares dotted numeric version strings, e.g. "1.10.2" vs "1.9.0".
const isNewerVersion = (candidate: string, current: string): boolean => {
  const a = candidate.split('.').map((n) => parseInt(n, 10) || 0);
  const b = current.split('.').map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const diff = (a[i] || 0) - (b[i] || 0);
    if (diff !== 0) return diff > 0;
  }
  return false;
};

const UpdatePrompt: React.FC = () => {
  const [show, setShow] = useState(false);
  const [nativeLatestVersion, setNativeLatestVersion] = useState<string | null>(null);

  const checkNative = useCallback(async () => {
    try {
      const res = await fetch(ITUNES_LOOKUP_URL, { cache: 'no-store' });
      if (!res.ok) return;
      const data = (await res.json()) as { results?: Array<{ version?: unknown }> };
      const latest = data.results?.[0]?.version;
      if (typeof latest !== 'string' || !isNewerVersion(latest, __APP_VERSION__)) return;
      if (localStorage.getItem(NATIVE_DISMISS_KEY) === latest) return;
      setNativeLatestVersion(latest);
      setShow(true);
    } catch {
      // Offline or App Store lookup unavailable — ignore.
    }
  }, []);

  const checkWeb = useCallback(async () => {
    // Only meaningful for deployed builds (no version.json in dev).
    if (!import.meta.env.PROD) return;
    try {
      const res = await fetch(VERSION_URL, { cache: 'no-store' });
      if (!res.ok) return;
      const data = (await res.json()) as { buildId?: unknown };
      const latest = typeof data.buildId === 'string' ? data.buildId : null;
      if (!latest || latest === __BUILD_ID__) return;
      // Don't keep nagging for the same build once dismissed this session.
      if (sessionStorage.getItem(DISMISS_KEY) === latest) return;
      setShow(true);
    } catch {
      // Offline or version.json unavailable — ignore.
    }
  }, []);

  const check = isNativeIOS ? checkNative : checkWeb;

  useEffect(() => {
    check();
    const onFocus = () => check();
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    // Safety-net poll for long-lived sessions.
    const interval = window.setInterval(check, 30 * 60 * 1000);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(interval);
    };
  }, [check]);

  if (!show) return null;

  const handleUpdate = () => {
    if (isNativeIOS) {
      window.open(APP_STORE_URL, '_blank');
      return;
    }
    window.location.reload();
  };

  const handleDismiss = async () => {
    if (isNativeIOS) {
      if (nativeLatestVersion) {
        localStorage.setItem(NATIVE_DISMISS_KEY, nativeLatestVersion);
      }
      setShow(false);
      return;
    }
    try {
      const res = await fetch(VERSION_URL, { cache: 'no-store' });
      const data = (await res.json().catch(() => null)) as { buildId?: unknown } | null;
      if (data && typeof data.buildId === 'string') {
        sessionStorage.setItem(DISMISS_KEY, data.buildId);
      }
    } catch {
      // ignore
    }
    setShow(false);
  };

  return (
    <div
      className="fixed bottom-20 sm:bottom-4 left-1/2 -translate-x-1/2 z-[1500] bg-card-bg text-text-primary px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 max-w-[92vw]"
      role="status"
      aria-live="polite"
    >
      <span className="text-sm">
        {isNativeIOS
          ? 'A new version of Ground Rounds is available on the App Store.'
          : 'A new version of Ground Rounds is available.'}
      </span>
      <Button onClick={handleUpdate} className="text-sm px-3 py-1">
        Update
      </Button>
      <button
        onClick={handleDismiss}
        className="text-gray-400 hover:text-text-primary ml-1"
        aria-label="Dismiss update notice"
      >
        ✕
      </button>
    </div>
  );
};

export default UpdatePrompt;
